// The agents' own conversations, pure (MoET review, 16 Oct 2026; Q20–Q23), so that node --test can bundle it (agent/moduleAgent.test.mjs):
// which agents talk on their own page (the Librarian since 22 Oct 2026 — Ikram's email of 20 Oct: "all capabilities and features of each
// agent accessible through the chatbot" —, Economic Radar, Country360, Economic Modelling, News Watch; Policy (PMO) is supplied by the PMO
// and never answers here), the agent a path belongs to (the Librarian's pages are `/knowledge/*`), the `?chat=<thread>` parameter that opens
// a conversation in place (every other parameter kept: Country360's `?country=`), the Librarian's conversation about one theme
// (`kb:theme:<topic>`), and each agent's suggested prompts — the same list under its box on its page and on `/agent?agent=<key>`. A
// templated prompt (`{{country}}`, `{{emirate}}`) fills the box instead of sending: with the page's value at the end of the caret, else with
// a default the person types over (it is selected).

/** The agents in Ikram's order; `policylab` is the one without a conversation of its own on its page. */
export const AGENT_MODULES = ["kb", "uae-radar", "country360", "policylab", "outlook", "global-radar"] as const;
export type AgentModule = (typeof AGENT_MODULES)[number];

/** The agents a person talks to on their own page and on `/agent?agent=<key>`. */
export const CHAT_MODULES = ["kb", "uae-radar", "country360", "outlook", "global-radar"] as const;
export type ChatModule = (typeof CHAT_MODULES)[number];

export const isAgentModule = (key: string | null | undefined): key is AgentModule => !!key && (AGENT_MODULES as readonly string[]).includes(key);
export const isChatModule = (key: string | null | undefined): key is ChatModule => !!key && (CHAT_MODULES as readonly string[]).includes(key);

/** The Librarian's own pages under `/knowledge` (the old `/knowledge/<collection>` links are not hers). */
const KB_PAGES = ["files", "themes", "p", "d", "search", "reviews", "manage", "how", "upload", "statistics"];

/** The agent whose page a path is (`/m/uae-radar/sectors` → `uae-radar`, `/knowledge/themes/tourism` → `kb`); null off the agents' pages. */
export function moduleOfPath(pathname: string): AgentModule | null {
  if (pathname === "/knowledge" || pathname === "/knowledge/") return "kb";
  if (pathname.startsWith("/knowledge/")) return KB_PAGES.includes(pathname.slice("/knowledge/".length).split("/")[0]) ? "kb" : null;
  const m = /^\/m\/([a-z0-9-]+)(?:\/|$)/.exec(pathname);
  const key = m?.[1] ?? null;
  return key && key !== "kb" && isAgentModule(key) ? key : null;
}

/** A theme's key as it may stand in a route or a thread (`circular-economy`; nothing that could be a path). */
export const themeKey = (value: string | null | undefined): string | null => (value && /^[a-z0-9][a-z0-9-]{0,95}$/.test(value) ? value : null);

/** Who a new conversation is with: the agent, or the Librarian about one theme (`kb:theme:<topic>`, a theme's page). */
export const agentThreadKey = (module: AgentModule, theme?: string | null): string => (module === "kb" && themeKey(theme) ? `kb:theme:${theme}` : module);

/** The agent's page, where a conversation asked for from a page without a chat box of its own opens. */
export const moduleHome = (module: AgentModule): string => (module === "kb" ? "/knowledge" : `/m/${module}`);

/** The query with the conversation open (`chat=<id>`) or closed (null); every other parameter is kept, in its order. */
export function withChat(search: string | URLSearchParams, id: string | null): URLSearchParams {
  const next = new URLSearchParams(search);
  if (id) next.set("chat", id);
  else next.delete("chat");
  return next;
}

/** A thread id as it may stand in `?chat=` (a GUID or a short key; nothing that could be a path). */
export const chatParam = (value: string | null | undefined): string | null => (value && /^[A-Za-z0-9_-]{1,64}$/.test(value) ? value : null);

/**
 * History state of a conversation opened in place: the page (pathname) it was opened on. Closing it on that same page goes back (the
 * step that opened it); a conversation that arrived from another page (an indicator asking the agent) closes in place on the agent's page,
 * so Close and ⌘J never leave it.
 */
export interface ChatState { econicChat?: string }
export const chatState = (pathname: string): ChatState => ({ econicChat: pathname });
export const closeGoesBack = (state: unknown, pathname: string): boolean =>
  !!state && typeof state === "object" && (state as ChatState).econicChat === pathname;

// ---------------------------------------------------------------- the question waiting for a conversation

/** A question for a conversation shown in place (the SDK's PanelRequest, by its id and words). */
export interface WaitingRequest { id: string; text: string }

/**
 * The next question of each conversation shown in place, by thread. It stays until the conversation has sent it (`sent`): a card closed
 * or opened full screen before its thread loaded still asks it once (`take` hands it to the full screen), and one opened again never
 * asks it twice. A newer question for the same thread replaces one not yet sent.
 */
export function createRequestStore<R extends WaitingRequest>(onChange: () => void = () => undefined) {
  const byThread = new Map<string, R>();
  return {
    put(threadId: string, r: R): void { byThread.set(threadId, r); onChange(); },
    peek(threadId: string | null | undefined): R | null { return threadId ? byThread.get(threadId) ?? null : null; },
    take(threadId: string | null | undefined): R | null {
      const r = threadId ? byThread.get(threadId) ?? null : null;
      if (r && threadId) { byThread.delete(threadId); onChange(); }
      return r;
    },
    sent(threadId: string, id: string): void {
      if (byThread.get(threadId)?.id === id) { byThread.delete(threadId); onChange(); }
    },
  };
}

/**
 * Whether a conversation has sent its request, one render at a time. The SDK's panel sends a request as soon as its thread has loaded
 * and no turn is streaming, and the turn streams at once; so a request is sent once it was seen idle (armed) and a turn then streams.
 * A request that arrives while an earlier turn streams waits for that turn to end first.
 */
export function requestWatchStep(armed: string | null, s: { request: string | null; streaming: boolean; loading: boolean }): { armed: string | null; sent: string | null } {
  if (!s.request) return { armed: null, sent: null };
  if (!s.streaming) return { armed: s.loading ? armed : s.request, sent: null };
  return armed === s.request ? { armed: null, sent: s.request } : { armed, sent: null };
}

// ---------------------------------------------------------------- the prompts

export type PromptVar = "country" | "emirate";

export interface ModulePromptDef {
  id: string;
  /** The variable the sentence names; the prompt then fills the box instead of sending. */
  slot?: PromptVar;
  /** Shown only while the variable is (or is not) known: Country360's prompts before and after a country is chosen. */
  when?: "set" | "unset";
  /** The turn's directive when the prompt is sent (a draft's `next <kind>`). */
  directive?: string;
  /** The start of a sentence the person completes (it fills): its chip says `<id>.label`, the box gets `<id>.text`. */
  stem?: boolean;
}

/**
 * Q23, in the Ministry's words (`econic.agent.modulePrompts.<module>.<id>`). Policy (PMO)'s are its frozen page's (WP-D): each fills the
 * box with the start of a sentence and carries the draft's directive for "Draft with ECONIC". Each agent's prompts span different areas of
 * the Ministry's mandate (22 Oct 2026: tourism, aviation, IP, SMEs, food, prices, regulations, the economy, growth), the regional
 * escalation at most once.
 */
export const MODULE_PROMPTS: Readonly<Record<AgentModule, readonly ModulePromptDef[]>> = {
  // The Librarian (22 Oct 2026): "you can ask it: is there an AI policy in the ministry?" (21 Oct), then the Ministry's own themes across its
  // mandate: tourism, trademarks, SMEs.
  kb: [{ id: "aiPolicy" }, { id: "tourism" }, { id: "trademarks" }, { id: "smes" }],
  "uae-radar": [
    { id: "inflation" },
    { id: "emirate", slot: "emirate" },
    { id: "tourism" },
    { id: "sectors" },
  ],
  // Its purpose first, from the moment the page opens (16 Oct 2026): screening the UAE's bilateral activity with a country, the country
  // selected for the person to type over until one is chosen.
  country360: [
    { id: "bilateral", slot: "country" },
    { id: "briefing", slot: "country" },
    { id: "changed", slot: "country", when: "set" },
    { id: "partners", when: "unset" },
  ],
  policylab: [
    { id: "policy", stem: true, directive: "next policy" },
    { id: "review", stem: true, directive: "next review" },
    { id: "amendment", stem: true, directive: "next amendment" },
    { id: "decision", stem: true, directive: "next decision" },
  ],
  // The Modeller's own abilities (22 Oct 2026) on the mandate's areas: a what-if on tourism, scenarios on the cost of living, the sensitivity,
  // an SME credit squeeze (comparing forecasts is an output).
  outlook: [{ id: "tourism" }, { id: "prices" }, { id: "sensitivity" }, { id: "sme" }],
  // News Watch across the Ministry's mandate (22 Oct 2026): tourism and air travel, food prices and the cost of living, SMEs and IP, and the
  // regional and global risks (the one escalation prompt).
  "global-radar": [{ id: "tourism" }, { id: "prices" }, { id: "smes" }, { id: "risks" }],
};

export interface ModulePrompt {
  id: string;
  text: string;
  /** The chip's words when they differ from the text (a stem's "Draft a policy on …"). */
  label: string | null;
  /** Put the text in the box (the caret at its end, or `select` chosen) instead of sending it. */
  fill: boolean;
  /** The characters to select once filled: the default value the person types over. */
  select: [number, number] | null;
  directive: string | null;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** A private-use mark that no translation carries: where the variable lands in the translated sentence. */
const MARK = "";

/** The sentence with the value in place, and where the value sits when it is the default (to be selected). */
export function fillSlot(template: string, value: string, select: boolean): { text: string; select: [number, number] | null } {
  const at = template.indexOf(MARK);
  if (at < 0) return { text: template, select: null };
  const text = template.slice(0, at) + value + template.slice(at + MARK.length);
  return { text, select: select ? [at, at + value.length] : null };
}

/**
 * An agent's prompts with the page's values (`{ country: "China" }`); a variable the page does not know takes its default
 * (`modulePrompts.defaults.<var>`), selected so that typing replaces it, and a prompt that needs a country no one has chosen is left out.
 */
export function modulePromptList(module: string, vars: Partial<Record<PromptVar, string | null | undefined>>, t: Translate): ModulePrompt[] {
  if (!isAgentModule(module)) return [];
  const out: ModulePrompt[] = [];
  for (const def of MODULE_PROMPTS[module]) {
    const key = `assistant:econic.agent.modulePrompts.${module}.${def.id}`;
    const known = def.slot ? vars[def.slot]?.trim() || null : null;
    if (def.when === "set" && !(def.slot ? known : true)) continue;
    if (def.when === "unset" && Object.values(vars).some((v) => v && v.trim())) continue;
    if (def.slot) {
      const value = known ?? t(`assistant:econic.agent.modulePrompts.defaults.${def.slot}`);
      const filled = fillSlot(t(key, { [def.slot]: MARK }), value, !known);
      out.push({ id: `${module}-${def.id}`, text: filled.text, label: null, fill: true, select: filled.select, directive: def.directive ?? null });
    } else if (def.stem) {
      out.push({ id: `${module}-${def.id}`, text: t(`${key}.text`), label: t(`${key}.label`), fill: true, select: null, directive: def.directive ?? null });
    } else {
      out.push({ id: `${module}-${def.id}`, text: t(key), label: null, fill: false, select: null, directive: def.directive ?? null });
    }
  }
  return out;
}
